import React, { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel, getSortedRowModel, useReactTable, type ColumnDef, type SortingState, type Table } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { AppTable, AppTableHorizontalScrollControls, Button, Dropdown, ProgressBar, ProgressSpinner, useAppTableHorizontalScroll } from '@allygpo/ally-ux';

import ApiService, { handleApiError } from '../../services/api';
import { useScenarioContext } from '../../context/ScenarioContext';
import { ProductComparisonRow } from '../../types';
import { count, money, pct } from '../../utils/format';
import { Card, Field, FilterBand, PageHeader, alignEnd, headerEnd, orUndefined, sortableAs, tableView } from '../../components/ui';

// Families arrive lower-case from the API; Results shows them capitalized, so Home matches.
const displayName = (name: string) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : name);

/**
 * The mix table with its scroll controls (narrow screens scroll it sideways; the controls show only when it
 * overflows). Its own component so the scroll hook runs when the table mounts, after the mix loads.
 */
const MixTable: React.FC<{ table: Table<ProductComparisonRow>; footerSlot: React.ReactNode }> = ({ table, footerSlot }) => {
  const horizontalScroll = useAppTableHorizontalScroll();
  return (
    <AppTable
      isBordered={false}
      ariaLabel="Observed product mix"
      table={table}
      tableLayout="auto"
      horizontalScroll={horizontalScroll}
      headerSlot={horizontalScroll.isOverflowingHorizontally ? <AppTableHorizontalScrollControls horizontalScroll={horizontalScroll} /> : undefined}
      footerSlot={footerSlot}
    />
  );
};

/** What the mix cells read from `meta.view`: the largest share, so bars scale to it. */
interface MixView { maxShare: number }

const mixColumns: ColumnDef<ProductComparisonRow>[] = [
  {
    id: 'name',
    accessorFn: (row) => orUndefined(row.product_family_name),
    ...sortableAs('text'),
    size: 140,
    header: 'Product family',
    cell: ({ row: { original: row } }) => <span className="font-medium min-w-0">{displayName(row.product_family_name)}</span>,
  },
  {
    id: 'mix',
    accessorFn: (row) => orUndefined(row.current_distribution),
    ...sortableAs('number'),
    size: 170,
    header: 'Current mix',
    cell: ({ row: { original: row }, table }) => {
      const { maxShare } = tableView<MixView, ProductComparisonRow>(table);
      const share = Number(row.current_distribution || 0);
      return <span className="flex align-items-center u-gap-2 min-w-0"><ProgressBar value={maxShare ? Math.min(100, (share / maxShare) * 100) : 0} className="w-8rem flex-none" aria-hidden="true" /><span>{pct(share)}</span></span>;
    },
  },
  { id: 'units', accessorFn: (row) => orUndefined(row.category_units), ...sortableAs('number'), size: 100, header: () => headerEnd('Category units'), meta: alignEnd, cell: ({ row: { original: row } }) => count(row.category_units) },
  { id: 'price', accessorFn: (row) => orUndefined(row.net_price_per_eu), ...sortableAs('number'), size: 100, header: () => headerEnd('Net price / EU'), meta: alignEnd, cell: ({ row: { original: row } }) => money(row.net_price_per_eu) },
  { id: 'rebate', accessorFn: (row) => orUndefined(row.rebate_per_eu), ...sortableAs('number'), size: 90, header: () => headerEnd('Rebate / EU'), meta: alignEnd, cell: ({ row: { original: row } }) => (row.rebate_per_eu ? money(row.rebate_per_eu) : '—') },
  {
    id: 'ncr',
    accessorFn: (row) => orUndefined(row.ncr_per_eu),
    ...sortableAs('number'),
    size: 120,
    header: () => headerEnd('Medicare NCR / EU'),
    meta: alignEnd,
    cell: ({ row: { original: row } }) => <span className={`siq-key-cell font-semibold${row.is_negative ? ' siq-neg text-danger' : ''}`}>{money(row.ncr_per_eu)}</span>,
  },
];


/** Home is the current product mix, not a shortcut to scenario generation. */
const Dashboard: React.FC = () => {
  const { state } = useScenarioContext();
  const navigate = useNavigate();
  // The practice is chosen in the top bar (Layout → PracticePicker); Home follows it.
  const account = state.generatorState.form.logged_in_user ?? '';
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState<ProductComparisonRow[]>([]);
  const [mixNcr, setMixNcr] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account) return;
    setError(null);
    ApiService.getUserCategories(account).then((response) => {
      const available = response.data?.categories ?? [];
      setCategories(available);
      // Default to the first area the account actually has. There is deliberately no
      // preferred therapeutic area here: this used to regex-match PD-1/PD-L1 and fall
      // back to available[0], which silently privileged one area for every practice
      // regardless of what they order. An existing selection still wins.
      setCategory((current) => available.includes(current) ? current : available[0] ?? '');
    }).catch((requestError) => setError(handleApiError(requestError)));
  }, [account]);

  useEffect(() => {
    if (!account || !category) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    ApiService.getProductComparison(account, category, controller.signal)
      .then((response) => { setRows(response.rows.filter((row) => row.has_volume)); setMixNcr(response.current_mix_ncr_per_eu ?? null); })
      .catch((requestError) => { if (!controller.signal.aborted) setError(handleApiError(requestError)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [account, category]);

  const totalShare = useMemo(() => rows.reduce((sum, row) => sum + Number(row.current_distribution || 0), 0), [rows]);
  const totalUnits = useMemo(() => rows.reduce((sum, row) => sum + Number(row.category_units || 0), 0), [rows]);
  const maxShare = useMemo(() => Math.max(0, ...rows.map((row) => Number(row.current_distribution || 0))), [rows]);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'mix', desc: true }]);
  const mixTable = useReactTable({
    data: rows,
    columns: mixColumns,
    meta: { view: { maxShare } satisfies MixView },
    state: { sorting },
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    getRowId: (row) => row.product_family_id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const familyCount = rows.length;
  const ncrClass = mixNcr !== null && mixNcr < 0 ? 'text-danger' : 'text-default';

  return <div className="dashboard-container flex flex-column u-gap-4">
    <PageHeader
      title="Current mix and NCR"
      subtitle="Review this practice's quarter to date product mix. Start scenarios from New run."
      actions={<Button onClick={() => navigate('/generate')}><i className="pi pi-plus" aria-hidden="true" />New run</Button>}
    />
    <FilterBand>
      <Field label="Therapeutic area" htmlFor="home-area">
        <Dropdown inputId="home-area" value={category} options={categories.map((value) => ({ label: value, value }))} onChange={(event) => setCategory(String(event.value ?? ''))} disabled={!account || categories.length === 0} placeholder="No areas" className="w-11rem" />
      </Field>
    </FilterBand>
    <Card
      title="Observed product mix"
      subtitle={category && !loading && !error ? `${category} · ${familyCount} product ${familyCount === 1 ? 'family' : 'families'} · quarter to date` : category || 'Quarter to date'}
      actions={!loading && !error && rows.length > 0 && <div className="flex align-items-baseline u-gap-2 text-xs text-tertiary"><span>Current Medicare NCR</span><strong className={`text-xl font-semibold ${ncrClass}`}>{money(mixNcr)}</strong><span>per EU</span></div>}
    >
      {loading && <div className="flex align-items-center justify-content-center u-gap-3 u-p-4 text-xs text-center min-h-9rem text-tertiary"><ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="5" /><span>Loading current product mix…</span></div>}
      {!loading && error && <div className="flex align-items-center justify-content-center u-gap-3 u-p-4 text-xs text-center min-h-9rem text-danger"><strong>Current mix could not load.</strong> {error}</div>}
      {!loading && !error && !rows.length && <div className="flex align-items-center justify-content-center u-gap-2 u-p-5 text-xs text-center text-tertiary">No orders this quarter for this practice.<Button variant="primary-text" onClick={() => navigate('/generate')}>New run</Button></div>}
      {!loading && !error && rows.length > 0 && <MixTable
        table={mixTable}
        footerSlot={<span>
          <strong>Total</strong> · {pct(totalShare)} · {count(totalUnits)} category units · Medicare NCR <strong className={ncrClass}>{money(mixNcr)}</strong> per EU
        </span>}
      />}
    </Card>
  </div>;
};

export default Dashboard;
